import {
  getRobotIconName,
  loadAllRobotImages,
  registerRobotStyleImageMissingHandler,
} from 'components/RobotOnMapEditable/robotMapUtils'
import { FeatureCollection, Point } from 'geojson'
import {
  loadServiceCalibPinImages,
  registerServiceCalibStyleImageMissingHandler,
  SERVICE_CALIB_MAP_ICON_PX,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibMapIcons'
import {
  FACILITY_DIALOG_MAP_ICON,
  FacilityDialogContextPin,
} from 'pages/Assets/shared/useFacilityDialogGeoContext'
import { useEffect, useMemo } from 'react'
import { Layer, Source } from 'react-map-gl'
import { ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { getYawFromQuaternion } from 'utils/getYawFromQuaternion'
import { IRobotInfo } from 'utils/interfaces'
import { robotIconFeatureProps, robotIconOffsetFromFeature, robotIconSizeFromFeature } from 'utils/robotIconLayout'

const FACILITY_SOURCE_ID = 'facility-dialog-facility'
const FACILITY_LAYER_ID = 'facility-dialog-facility-symbols'
const CONTEXT_SOURCE_ID = 'facility-dialog-context'
const CONTEXT_LAYER_ID = 'facility-dialog-context-symbols'
const ROBOT_SOURCE_ID = 'facility-dialog-robots'
const ROBOT_LAYER_ID = 'facility-dialog-robot-symbols'

/** 2× calib pin logical size (42px → 84px). */
const FACILITY_MAP_ICON_PX = SERVICE_CALIB_MAP_ICON_PX * 2
const FACILITY_MAP_ICON_RASTER_PX = FACILITY_MAP_ICON_PX * 2

const CONTEXT_ICON_SIZE: mapboxgl.Expression = [
  'interpolate',
  ['exponential', 2],
  ['zoom'],
  14,
  0.55,
  22,
  2.4,
]

const FACILITY_ICON_URL = `${process.env.PUBLIC_URL || ''}/service-calib-map/facility_map.svg`

function loadFacilityMapIcon(map: any | undefined): Promise<void> {
  if (!map) {
    return Promise.resolve()
  }
  return new Promise(resolve => {
    if (map.hasImage(FACILITY_DIALOG_MAP_ICON)) {
      resolve()
      return
    }
    const img = new Image()
    img.onload = () => {
      try {
        if (map.hasImage(FACILITY_DIALOG_MAP_ICON)) {
          resolve()
          return
        }
        // Same pipeline as service-calib pins: fixed logical size + pixelRatio 2.
        const canvas = document.createElement('canvas')
        canvas.width = FACILITY_MAP_ICON_RASTER_PX
        canvas.height = FACILITY_MAP_ICON_RASTER_PX
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.clearRect(0, 0, FACILITY_MAP_ICON_RASTER_PX, FACILITY_MAP_ICON_RASTER_PX)
          // Small inset so anti-aliased stroke edges are not clipped by the canvas.
          const inset = Math.round(FACILITY_MAP_ICON_RASTER_PX * 0.04)
          const draw = FACILITY_MAP_ICON_RASTER_PX - inset * 2
          ctx.drawImage(img, inset, inset, draw, draw)
          void createImageBitmap(canvas).then(bitmap => {
            if (!map.hasImage(FACILITY_DIALOG_MAP_ICON)) {
              map.addImage(FACILITY_DIALOG_MAP_ICON, bitmap, { pixelRatio: 2 })
            }
            resolve()
          })
          return
        }
        map.addImage(FACILITY_DIALOG_MAP_ICON, img, { pixelRatio: 2 })
      } catch (e) {
        console.warn('[FacilityDialogGeoOverlay] addImage facility failed', e)
      }
      resolve()
    }
    img.onerror = () => {
      console.warn('[FacilityDialogGeoOverlay] failed to load facility_map.svg')
      resolve()
    }
    img.src = FACILITY_ICON_URL
  })
}

/**
 * Read-only facility context pins for Station/MP dialogs (UX-DIALOG-GEO):
 * facility icon (bottom, 2×), other stations/MPs, robots (live lastPosition or static geo_point).
 */
export default function FacilityDialogGeoOverlay({
  map,
  contextPins,
  robots,
}: {
  map: any | undefined
  contextPins: FacilityDialogContextPin[]
  robots: IRobotInfo[]
}) {
  const facilityData = useMemo<FeatureCollection<Point>>(
    () => ({
      type: 'FeatureCollection',
      features: contextPins
        .filter(pin => pin.kind === 'facility')
        .map(pin => ({
          type: 'Feature' as const,
          geometry: pin.point,
          properties: {
            iconId: pin.iconId,
          },
        })),
    }),
    [contextPins]
  )

  const otherContextData = useMemo<FeatureCollection<Point>>(
    () => ({
      type: 'FeatureCollection',
      features: contextPins
        .filter(pin => pin.kind !== 'facility')
        .map(pin => ({
          type: 'Feature' as const,
          geometry: pin.point,
          properties: {
            iconId: pin.iconId,
            opacity: 0.75,
          },
        })),
    }),
    [contextPins]
  )

  const robotData = useMemo<FeatureCollection<Point>>(() => {
    return {
      type: 'FeatureCollection',
      features: robots
        .filter(robot => robot.lastPosition?.coordinates && robot.lastPosition.coordinates.length >= 2)
        .map(robot => ({
          type: 'Feature' as const,
          geometry: robot.lastPosition as Point,
          properties: {
            icon: getRobotIconName(robot.vendor, robot.robot_color, robot.robot_type),
            yaw: robot.lastOrientation ? getYawFromQuaternion(robot.lastOrientation) : 0,
            opacity: robot.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE ? 0.95 : 0.55,
            ...robotIconFeatureProps(robot.lengthM, robot.originToFrontM, robot.lastPosition?.coordinates?.[1]),
          },
        })),
    }
  }, [robots])

  useEffect(() => {
    if (!map) {
      return
    }
    void loadServiceCalibPinImages(map)
    void loadFacilityMapIcon(map)
    void loadAllRobotImages(map)
    const unregisterCalib = registerServiceCalibStyleImageMissingHandler(map)
    const unregisterRobots = registerRobotStyleImageMissingHandler(map)
    const onStyleLoad = () => {
      void loadServiceCalibPinImages(map)
      void loadFacilityMapIcon(map)
      void loadAllRobotImages(map)
    }
    map.on('style.load', onStyleLoad)
    return () => {
      unregisterCalib()
      unregisterRobots()
      map.off('style.load', onStyleLoad)
    }
  }, [map])

  return (
    <>
      {/* Facility first → under stations/MPs, robots, and the editable pin. */}
      {facilityData.features.length > 0 && (
        <Source id={FACILITY_SOURCE_ID} type="geojson" data={facilityData}>
          <Layer
            id={FACILITY_LAYER_ID}
            type="symbol"
            layout={{
              'icon-image': ['get', 'iconId'],
              'icon-anchor': 'center',
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'icon-size': CONTEXT_ICON_SIZE,
            }}
            paint={{
              'icon-opacity': 1,
            }}
          />
        </Source>
      )}
      {otherContextData.features.length > 0 && (
        <Source id={CONTEXT_SOURCE_ID} type="geojson" data={otherContextData}>
          <Layer
            id={CONTEXT_LAYER_ID}
            type="symbol"
            layout={{
              'icon-image': ['get', 'iconId'],
              'icon-anchor': 'center',
              'icon-allow-overlap': true,
              'icon-size': CONTEXT_ICON_SIZE,
            }}
            paint={{
              'icon-opacity': ['get', 'opacity'],
            }}
          />
        </Source>
      )}
      {robotData.features.length > 0 && (
        <Source id={ROBOT_SOURCE_ID} type="geojson" data={robotData}>
          <Layer
            id={ROBOT_LAYER_ID}
            type="symbol"
            layout={{
              'icon-image': ['get', 'icon'],
              'icon-anchor': 'center',
              'icon-rotation-alignment': 'map',
              'icon-rotate': ['get', 'yaw'],
              'icon-allow-overlap': true,
              'icon-size': robotIconSizeFromFeature as any,
              'icon-offset': robotIconOffsetFromFeature as any,
            }}
            paint={{
              'icon-opacity': ['get', 'opacity'],
            }}
          />
        </Source>
      )}
    </>
  )
}
